import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'

// Goal Row Slices
export const GOAL_RENAME_ACTION_ID = 'goal.rename'
export const GOAL_REPRIORITIZE_ACTION_ID = 'goal.reprioritize'
export const GOAL_LINK_ACTION_ID = 'goal.link'
export const GOAL_DELETE_ACTION_ID = 'goal.delete'
export const GOAL_CONNECTIONS_ACTION_ID = 'goal.show-connections'

// Task Card Slices
export const TASK_COMPLETE_ACTION_ID = 'task.complete'
export const TASK_DUPLICATE_ACTION_ID = 'task.duplicate'
export const TASK_MOVE_ACTION_ID = 'task.move'
export const TASK_LINK_ACTION_ID = 'task.link'
export const TASK_DELETE_ACTION_ID = 'task.delete'

// Marker Slices
export const MARKER_EDIT_ACTION_ID = 'marker.edit'
export const MARKER_SHIFT_ACTION_ID = 'marker.shift'
export const MARKER_DEPENDENCIES_ACTION_ID = 'marker.show-dependencies'
export const MARKER_DELETE_ACTION_ID = 'marker.delete'

// Canvas Slices
export const DASHBOARD_CANVAS_NEW_GOAL_ACTION_ID = 'dashboard.canvas.new-goal'
export const DASHBOARD_CANVAS_NEW_TASK_ACTION_ID = 'dashboard.canvas.new-task'
export const DASHBOARD_CANVAS_REALIGN_ACTION_ID = 'dashboard.canvas.realign'
export const DASHBOARD_CANVAS_CONNECTIONS_ACTION_ID = 'dashboard.canvas.show-connections'

export type DashboardOrbitTargetArgs = {
  id?: string
  title?: string
}

const dashboardOrbitTargetArgsSchema: JSONSchemaType<DashboardOrbitTargetArgs> = {
  type: 'object',
  properties: {
    id: { type: 'string', nullable: true },
    title: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: true,
}

const TARGET_REQUIRED_ACTION_IDS = new Set([
  GOAL_RENAME_ACTION_ID,
  GOAL_REPRIORITIZE_ACTION_ID,
  GOAL_LINK_ACTION_ID,
  GOAL_DELETE_ACTION_ID,
  GOAL_CONNECTIONS_ACTION_ID,
  TASK_COMPLETE_ACTION_ID,
  TASK_DUPLICATE_ACTION_ID,
  TASK_MOVE_ACTION_ID,
  TASK_LINK_ACTION_ID,
  TASK_DELETE_ACTION_ID,
  MARKER_EDIT_ACTION_ID,
  MARKER_SHIFT_ACTION_ID,
  MARKER_DEPENDENCIES_ACTION_ID,
  MARKER_DELETE_ACTION_ID,
])

const makeOrbitDashboardAction = (
  id: string,
  label: string,
  description: string,
  domain: string,
  risk: 'READ_ONLY' | 'LOW' | 'MEDIUM' | 'HIGH',
  permissionAction?: 'timeline.create' | 'timeline.edit' | 'timeline.delete' | 'timeline.move_restore' | 'timeline.view',
): ActionDefinition<DashboardOrbitTargetArgs> => ({
  id,
  label,
  description,
  risk,
  domain,
  surfaces: ['orbit', 'catalog'],
  argsSchema: dashboardOrbitTargetArgsSchema,
  permission: permissionAction ? (context) => context.canPerform(permissionAction) : undefined,
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'

    if (TARGET_REQUIRED_ACTION_IDS.has(id) && !args.id) {
      const message = `Target ID is required for ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    const ops = context.dashboardOps
    let handler: (() => Promise<void> | void) | undefined

    if (id === GOAL_RENAME_ACTION_ID) {
      handler = ops?.renameGoal ? () => ops.renameGoal!(args.id!, args.title) : undefined
    } else if (id === GOAL_REPRIORITIZE_ACTION_ID) {
      handler = ops?.reprioritizeGoal ? () => ops.reprioritizeGoal!(args.id!) : undefined
    } else if (id === GOAL_LINK_ACTION_ID) {
      handler = ops?.linkGoal ? () => ops.linkGoal!(args.id!) : undefined
    } else if (id === GOAL_DELETE_ACTION_ID) {
      handler = ops?.deleteGoal ? () => ops.deleteGoal!(args.id!) : undefined
    } else if (id === GOAL_CONNECTIONS_ACTION_ID) {
      handler = ops?.showGoalConnections ? () => ops.showGoalConnections!(args.id!) : undefined
    } else if (id === TASK_COMPLETE_ACTION_ID) {
      handler = ops?.completeTask ? () => ops.completeTask!(args.id!) : undefined
    } else if (id === TASK_DUPLICATE_ACTION_ID) {
      handler = ops?.duplicateTask ? () => ops.duplicateTask!(args.id!) : undefined
    } else if (id === TASK_MOVE_ACTION_ID) {
      handler = ops?.moveTask ? () => ops.moveTask!(args.id!) : undefined
    } else if (id === TASK_LINK_ACTION_ID) {
      handler = ops?.linkTask ? () => ops.linkTask!(args.id!) : undefined
    } else if (id === TASK_DELETE_ACTION_ID) {
      handler = ops?.deleteTask ? () => ops.deleteTask!(args.id!) : undefined
    } else if (id === MARKER_EDIT_ACTION_ID) {
      handler = ops?.editMarker ? () => ops.editMarker!(args.id!) : undefined
    } else if (id === MARKER_SHIFT_ACTION_ID) {
      handler = ops?.shiftMarker ? () => ops.shiftMarker!(args.id!) : undefined
    } else if (id === MARKER_DEPENDENCIES_ACTION_ID) {
      handler = ops?.showMarkerDependencies ? () => ops.showMarkerDependencies!(args.id!) : undefined
    } else if (id === MARKER_DELETE_ACTION_ID) {
      handler = ops?.deleteMarker ? () => ops.deleteMarker!(args.id!) : undefined
    } else if (id === DASHBOARD_CANVAS_NEW_GOAL_ACTION_ID) {
      handler = ops?.createGoal ? () => ops.createGoal!() : undefined
    } else if (id === DASHBOARD_CANVAS_NEW_TASK_ACTION_ID) {
      handler = ops?.createTask ? () => ops.createTask!() : undefined
    } else if (id === DASHBOARD_CANVAS_REALIGN_ACTION_ID) {
      handler = ops?.realignCanvas ? () => ops.realignCanvas!() : undefined
    } else if (id === DASHBOARD_CANVAS_CONNECTIONS_ACTION_ID) {
      handler = ops?.showAllConnections ? () => ops.showAllConnections!() : undefined
    }

    if (!handler) {
      const message = `Operation for ${label} (${id}) is not available`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    try {
      await Promise.resolve(handler())
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to execute ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: true,
        correlationId,
      }
    }

    context.showToast?.(`${label}${args.title ? ` · ${args.title}` : ''}`, 'info')
    return {
      kind: 'COMPLETED',
      result: { id: args.id, title: args.title },
      message: `${label} dispatched`,
      correlationId,
    }
  },
})

export const goalRenameAction = makeOrbitDashboardAction(
  GOAL_RENAME_ACTION_ID,
  'Rename',
  'Renames a goal.',
  'goal',
  'LOW',
  'timeline.edit',
)

export const goalReprioritizeAction = makeOrbitDashboardAction(
  GOAL_REPRIORITIZE_ACTION_ID,
  'Reprioritize',
  'Changes priority for a goal.',
  'goal',
  'LOW',
  'timeline.edit',
)

export const goalLinkAction = makeOrbitDashboardAction(
  GOAL_LINK_ACTION_ID,
  'Link',
  'Links goal to another entity.',
  'goal',
  'LOW',
  'timeline.edit',
)

export const goalDeleteAction = makeOrbitDashboardAction(
  GOAL_DELETE_ACTION_ID,
  'Delete',
  'Deletes a goal.',
  'goal',
  'HIGH',
  'timeline.delete',
)

export const goalConnectionsAction = makeOrbitDashboardAction(
  GOAL_CONNECTIONS_ACTION_ID,
  'Show connections',
  'Shows connection network for a goal.',
  'goal',
  'READ_ONLY',
  'timeline.view',
)

export const taskCompleteAction = makeOrbitDashboardAction(
  TASK_COMPLETE_ACTION_ID,
  'Complete',
  'Marks task complete.',
  'task',
  'LOW',
  'timeline.edit',
)

export const taskDuplicateAction = makeOrbitDashboardAction(
  TASK_DUPLICATE_ACTION_ID,
  'Duplicate',
  'Duplicates a task.',
  'task',
  'LOW',
  'timeline.create',
)

export const taskMoveAction = makeOrbitDashboardAction(
  TASK_MOVE_ACTION_ID,
  'Move to…',
  'Moves task to another goal or board.',
  'task',
  'LOW',
  'timeline.move_restore',
)

export const taskLinkAction = makeOrbitDashboardAction(
  TASK_LINK_ACTION_ID,
  'Link',
  'Links task to another entity.',
  'task',
  'LOW',
  'timeline.edit',
)

export const taskDeleteAction = makeOrbitDashboardAction(
  TASK_DELETE_ACTION_ID,
  'Delete',
  'Deletes a task.',
  'task',
  'HIGH',
  'timeline.delete',
)

export const markerEditAction = makeOrbitDashboardAction(
  MARKER_EDIT_ACTION_ID,
  'Edit',
  'Edits milestone marker details.',
  'marker',
  'LOW',
  'timeline.edit',
)

export const markerShiftAction = makeOrbitDashboardAction(
  MARKER_SHIFT_ACTION_ID,
  'Shift',
  'Shifts milestone marker date.',
  'marker',
  'LOW',
  'timeline.edit',
)

export const markerDependenciesAction = makeOrbitDashboardAction(
  MARKER_DEPENDENCIES_ACTION_ID,
  'Show dependencies',
  'Shows dependencies for a milestone marker.',
  'marker',
  'READ_ONLY',
  'timeline.view',
)

export const markerDeleteAction = makeOrbitDashboardAction(
  MARKER_DELETE_ACTION_ID,
  'Delete',
  'Deletes a milestone marker.',
  'marker',
  'HIGH',
  'timeline.delete',
)

export const dashboardCanvasNewGoalAction = makeOrbitDashboardAction(
  DASHBOARD_CANVAS_NEW_GOAL_ACTION_ID,
  'New goal',
  'Creates a new goal on dashboard canvas.',
  'dashboard',
  'LOW',
  'timeline.create',
)

export const dashboardCanvasNewTaskAction = makeOrbitDashboardAction(
  DASHBOARD_CANVAS_NEW_TASK_ACTION_ID,
  'New task',
  'Creates a new task on dashboard canvas.',
  'dashboard',
  'LOW',
  'timeline.create',
)

export const dashboardCanvasRealignAction = makeOrbitDashboardAction(
  DASHBOARD_CANVAS_REALIGN_ACTION_ID,
  'Realign',
  'Realigns dashboard canvas items.',
  'dashboard',
  'LOW',
  'timeline.edit',
)

export const dashboardCanvasConnectionsAction = makeOrbitDashboardAction(
  DASHBOARD_CANVAS_CONNECTIONS_ACTION_ID,
  'Show all connections',
  'Shows all connection lines on dashboard canvas.',
  'dashboard',
  'READ_ONLY',
  'timeline.view',
)

export const registerDashboardActions = (): void => {
  if (!getAction(GOAL_RENAME_ACTION_ID)) registerAction(goalRenameAction)
  if (!getAction(GOAL_REPRIORITIZE_ACTION_ID)) registerAction(goalReprioritizeAction)
  if (!getAction(GOAL_LINK_ACTION_ID)) registerAction(goalLinkAction)
  if (!getAction(GOAL_DELETE_ACTION_ID)) registerAction(goalDeleteAction)
  if (!getAction(GOAL_CONNECTIONS_ACTION_ID)) registerAction(goalConnectionsAction)

  if (!getAction(TASK_COMPLETE_ACTION_ID)) registerAction(taskCompleteAction)
  if (!getAction(TASK_DUPLICATE_ACTION_ID)) registerAction(taskDuplicateAction)
  if (!getAction(TASK_MOVE_ACTION_ID)) registerAction(taskMoveAction)
  if (!getAction(TASK_LINK_ACTION_ID)) registerAction(taskLinkAction)
  if (!getAction(TASK_DELETE_ACTION_ID)) registerAction(taskDeleteAction)

  if (!getAction(MARKER_EDIT_ACTION_ID)) registerAction(markerEditAction)
  if (!getAction(MARKER_SHIFT_ACTION_ID)) registerAction(markerShiftAction)
  if (!getAction(MARKER_DEPENDENCIES_ACTION_ID)) registerAction(markerDependenciesAction)
  if (!getAction(MARKER_DELETE_ACTION_ID)) registerAction(markerDeleteAction)

  if (!getAction(DASHBOARD_CANVAS_NEW_GOAL_ACTION_ID)) registerAction(dashboardCanvasNewGoalAction)
  if (!getAction(DASHBOARD_CANVAS_NEW_TASK_ACTION_ID)) registerAction(dashboardCanvasNewTaskAction)
  if (!getAction(DASHBOARD_CANVAS_REALIGN_ACTION_ID)) registerAction(dashboardCanvasRealignAction)
  if (!getAction(DASHBOARD_CANVAS_CONNECTIONS_ACTION_ID)) registerAction(dashboardCanvasConnectionsAction)
}

registerDashboardActions()
